import { create } from "zustand";
import { tempEngagement, tempLeadEng } from "../api_layer/api";
import { todayEngagement } from "../api_layer/api";
import {
  leadSlotBookingEng,
  leadColdCallEng,
  leadReattemptEng,
  slotViewEngagement,
} from "../api_layer/api";
import moment from "moment";
import secureLocalStorage from "react-secure-storage";
import { RECEPTIONINST } from "modules/utilities/utility_2";
import { isEmpty } from "lodash";

const PAGE_SIZE = 10;

const getLeadDateRange = () => {
  const today = moment();
  const fifteenDaysAgo = today.clone().subtract(15, "days");
  return {
    start_date: `${fifteenDaysAgo.format("YYYY-MM-DD")}T18:30:00.000Z`,
    end_date: `${today.format("YYYY-MM-DD")}T18:29:59.999Z`,
  };
};

const useActionableListStore = create((set) => ({
  tempEngData: [],
  todayEngData: [],
  tempLeadData:[],
  tempLoading: false,
  todayLoading: false,
  tempLeadLoading:false,
  error: null,

  // Lead agent tab state
  slotBookingData: [],
  slotBookingTotal: 0,
  slotBookingLoading: false,
  coldCallData: [],
  coldCallTotal: 0,
  coldCallLoading: false,
  reattemptData: [],
  reattemptTotal: 0,
  reattemptLoading: false,

  // Today engagements (rep_id) - server-side paginated
  todayEngTotal: 0,
  fetchTodayEngData: async (tag = null, fromCrm = null, page = 1) => {
    try {
      const today = moment();
      const fifteenDaysAgo = today.clone().subtract(15, "days");

      set({ todayLoading: true, error: null });
      const response = await todayEngagement({
        start_date: `${fifteenDaysAgo.format("YYYY-MM-DD")}T18:30:00.000Z`,
        end_date: `${(RECEPTIONINST ? today.clone().add(7, 'days') : today).format("YYYY-MM-DD")}T18:29:59.999Z`,
        tag_filter: tag,
        from_crm: fromCrm,
        page,
        limit: PAGE_SIZE,
      });
      const engData = response.data?.data || [];
      set({
        todayEngData: isEmpty(engData) ? [] : engData,
        todayEngTotal: response.data?.total || 0,
        todayLoading: false,
      });
    } catch (error) {
      set({ error: error.message, todayLoading: false });
    }
  },

  // Temp engagements (temporary_rep_id) - server-side paginated
  tempEngTotal: 0,
  fetchTempEngData: async (tag = null, page = 1) => {
    try {
      const today = moment();
      const fifteenDaysAgo = today.clone().subtract(15, "days");

      set({ tempLoading: true, error: null });
      const response = await tempEngagement({
        tag_filter: tag,
        start_date: `${fifteenDaysAgo.format("YYYY-MM-DD")}T18:30:00.000Z`,
        end_date: `${today.format("YYYY-MM-DD")}T18:29:59.999Z`,
        page,
        limit: PAGE_SIZE,
      });
      const tempData = response.data?.data || [];
      set({
        tempEngData: tempData,
        tempEngTotal: response.data?.total || 0,
        tempLoading: false,
      });
    } catch (error) {
      set({ error: error.message, tempLoading: false });
    }
  },

  // Lead temp engagements
  fetchLeadTempData: async (page = 1) => {
    try {
      const today = moment();
      const fifteenDaysAgo = today.clone().subtract(15, "days");

      set({ tempLeadLoading: true, error: null });
      const response = await tempLeadEng({
        start_date: `${fifteenDaysAgo.format("YYYY-MM-DD")}T18:30:00.000Z`,
        end_date: `${today.format("YYYY-MM-DD")}T18:29:59.999Z`,
        page,
        limit: PAGE_SIZE,
      });
      const leadTempData = response.data?.data || [];
      set({
        tempLeadData: leadTempData,
        tempLeadLoading: false,
      });
    } catch (error) {
      set({ error: error.message, tempLeadLoading: false });
    }
  },

  // Slot view state (server-side filtered + paginated)
  slotViewData: [],
  slotViewTotal: 0,
  slotViewLoading: false,

  fetchSlotViewData: async (page = 1) => {
    try {
      set({ slotViewLoading: true, error: null });
      const { start_date, end_date } = getLeadDateRange();
      const repId = secureLocalStorage.getItem('id');

      const response = await slotViewEngagement({ rep_id: repId, start_date, end_date, page, limit: PAGE_SIZE });

      set({
        slotViewData: response.data?.data || [],
        slotViewTotal: response.data?.total || 0,
        slotViewLoading: false,
      });
    } catch (error) {
      set({ error: error.message, slotViewLoading: false });
    }
  },

  // Lead agent tab actions (server-side paginated)
  fetchLeadSlotBooking: async (page = 1) => {
    try {
      set({ slotBookingLoading: true, error: null });
      const { start_date, end_date } = getLeadDateRange();
      const repId = secureLocalStorage.getItem('id');

      const response = await leadSlotBookingEng({ rep_id: repId, start_date, end_date, page, limit: PAGE_SIZE });

      set({
        slotBookingData: response.data?.data || [],
        slotBookingTotal: response.data?.total || 0,
        slotBookingLoading: false,
      });
    } catch (error) {
      set({ error: error.message, slotBookingLoading: false });
    }
  },

  fetchLeadColdCall: async (page = 1) => {
    try {
      set({ coldCallLoading: true, error: null });
      const { start_date, end_date } = getLeadDateRange();
      const repId = secureLocalStorage.getItem('id');

      const response = await leadColdCallEng({ rep_id: repId, start_date, end_date, page, limit: PAGE_SIZE });

      set({
        coldCallData: response.data?.data || [],
        coldCallTotal: response.data?.total || 0,
        coldCallLoading: false,
      });
    } catch (error) {
      set({ error: error.message, coldCallLoading: false });
    }
  },

  fetchLeadReattempt: async (page = 1) => {
    try {
      set({ reattemptLoading: true, error: null });
      const { start_date, end_date } = getLeadDateRange();
      const repId = secureLocalStorage.getItem('id');

      const response = await leadReattemptEng({ rep_id: repId, start_date, end_date, page, limit: PAGE_SIZE });

      set({
        reattemptData: response.data?.data || [],
        reattemptTotal: response.data?.total || 0,
        reattemptLoading: false,
      });
    } catch (error) {
      set({ error: error.message, reattemptLoading: false });
    }
  },
}));

export default useActionableListStore;
